import { LabelsForInput } from 'components/LabelsForInputs'
import { TextArea } from 'components/TextArea'
import {
    Control,
    Controller,
    FieldErrors,
    FieldValues,
    UseFormRegister,
    UseFormWatch
} from 'react-hook-form'
import { NotificationType } from 'types/notification'
import { observationTextAreaStyles } from '../styles'
import {
    Role,
    listOfRoles
} from 'components/WithRoleAuthorization/WithRoleAuthorization'
import { canEditStepOneRoles } from '..'
import { ExclamationCircleIcon } from '@heroicons/react/outline'
import { IVariableReducer } from 'types/variable'
import { DAMAGE_LEVELS } from 'utils/damage'

interface NotificationGuidanceProps {
    notification: NotificationType
    register?: UseFormRegister<FieldValues>
    authProfile: Role
    showNotificationGuidance(): boolean
    control: Control<FieldValues>
    watch: UseFormWatch<FieldValues>
    errors?: FieldErrors<FieldValues>
    variableSelector?: IVariableReducer
    forceEnabled?: boolean
}

export const NotificationGuidance = ({
    notification,
    register,
    authProfile,
    showNotificationGuidance,
    watch,
    errors,
    control,
    variableSelector,
    forceEnabled = false
}: NotificationGuidanceProps) => {

    // @ts-ignore
    const variableNotificationClassification = variableSelector?.variablesList?.find((item) => item?.label === "Sobre o paciente")?.children?.find((item) => item?.label === "Classificação da notificação")
    const external = variableNotificationClassification?.choices?.[2]
    const internal = variableNotificationClassification?.choices?.[1]

    const isProfileCanViewGuidanceNotification =
        canEditStepOneRoles(authProfile)
    const isProfileCantEdit = authProfile === listOfRoles.location_qa
    // Se forceEnabled for true, permite edição mesmo que isProfileCantEdit seja true
    const canEdit = !isProfileCantEdit || forceEnabled

    const notificationIsExternal =
        notification?.u_incident_classification?.value === external?.value

    const uIncidentClassification = watch(
        variableNotificationClassification?.name
    )
    const uObservationIncidentClassification = watch(
        'u_observation_incident_classification'
    )
    
    if (!showNotificationGuidance()) {
        return null
    }

    const bgBoxColor = () => {
        const imageRisk = watch('u_image_risk');
        const damageLevel = watch('damage_level');
        const incidentClassificationExists = notification?.u_incident_classification?.value;

        const isImageRiskYes = imageRisk?.includes('yes');
        const isDamageLevelMatch = [DAMAGE_LEVELS[0].id, DAMAGE_LEVELS[1].id].includes(damageLevel);

        if (!isProfileCanViewGuidanceNotification) {
            return ''
        }

        if (incidentClassificationExists && isProfileCantEdit) {
            return '';
        }

        if (isProfileCantEdit && (isImageRiskYes || isDamageLevelMatch)) {
            return 'bg-[#F6F6F6]';
        }

        if (!isProfileCantEdit) {
            return 'bg-[#F6F6F6]';
        }

        return '';
    }
    const backgroundColor = bgBoxColor() || 'bg-white';
    
    return (
        <div
            className={`lg:col-span-1 col-span-2 rounded-lg p-6 border border-borderLine lg:ml-4 ${backgroundColor} h-[350px]`}
        >
            <div className="flex flex-col h-full">
                <p className="text-blueTheme text-sm font-bold">
                    Orientação da notificação
                </p>
                <span className="text-xs text-darkGray">
                    Campo destinado à Diretoria de Qualidade
                </span>
                <div className="h-px bg-borderLine mt-2" />

                <>
                    {isProfileCanViewGuidanceNotification ? (
                        <>
                            {canEdit ? (
                                <fieldset
                                    disabled={false}
                                    className="mt-2"
                                >
                                    <legend className="font-semibold text-xs text-darkGray">
                                        Notificação
                                    </legend>
                                    <div className="flex flex-row gap-2 relative">
                                        <div className="flex items-center gap-2">
                                            <Controller
                                                name={
                                                    variableNotificationClassification?.name
                                                }
                                                key={
                                                    notification?.sys_id?.value
                                                }
                                                control={control}
                                                defaultValue={
                                                    notification
                                                        ?.u_incident_classification
                                                        ?.value
                                                }
                                                rules={{
                                                    required:
                                                        'This field is required'
                                                }}
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        className={`text-yellow ${!uIncidentClassification &&
                                                            'border border-yellow'
                                                            }`}
                                                        type="radio"
                                                        id="Internal"
                                                        name={
                                                            variableNotificationClassification?.name
                                                        }
                                                        value={internal?.value}
                                                        defaultChecked={
                                                            notification
                                                                ?.u_incident_classification
                                                                ?.value ===
                                                            internal?.value
                                                        }
                                                    />
                                                )}
                                            />

                                            <label
                                                htmlFor="Internal"
                                                className="text-sm text-darkGray"
                                            >
                                                {internal?.label}
                                            </label>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Controller
                                                name={
                                                    variableNotificationClassification?.name
                                                }
                                                control={control}
                                                key={
                                                    notification?.sys_id?.value
                                                }
                                                defaultValue={
                                                    notification
                                                        ?.u_incident_classification
                                                        ?.value
                                                }
                                                rules={{
                                                    required:
                                                        'This field is required'
                                                }}
                                                render={({ field }) => (
                                                    <input
                                                        {...field}
                                                        className={`text-yellow ${!uIncidentClassification &&
                                                            'border border-yellow'
                                                            }`}
                                                        type="radio"
                                                        id="Externa"
                                                        name={
                                                            variableNotificationClassification?.name
                                                        }
                                                        value={external?.value}
                                                        defaultChecked={
                                                            notification
                                                                ?.u_incident_classification
                                                                ?.value ===
                                                            external?.value
                                                        }
                                                    />
                                                )}
                                            />

                                            <label
                                                htmlFor="Externa"
                                                className="text-sm text-darkGray"
                                            >
                                                {external?.label}
                                            </label>
                                        </div>

                                        <span className="absolute text-errorText text-xs -bottom-3.5">
                                            {errors.u_incident_classification &&
                                                'Preenchimento obrigatório.'}
                                        </span>
                                    </div>
                                    <div className="mt-6">
                                        <LabelsForInput
                                            title="Observação"
                                            subtitle=""
                                        />
                                        <TextArea
                                            error={
                                                errors.u_observation_incident_classification && {
                                                    message:
                                                        'Preenchimento obrigatório.'
                                                }
                                            }
                                            maxLength={100}
                                            defaultValue={
                                                notification
                                                    ?.u_observation_incident_classification
                                                    ?.display_value
                                            }
                                            name="u_observation_incident_classification"
                                            register={register}
                                            className={`${observationTextAreaStyles} ${!uObservationIncidentClassification &&
                                                'border border-amberAlt'
                                                }`}
                                            placeholder="Digite aqui"
                                            rows={8}
                                        />
                                    </div>
                                </fieldset>
                            ) : (
                                <GuidanceInfos
                                    notification={notification}
                                    notificationIsExternal={
                                        notificationIsExternal
                                    }
                                />
                            )}
                        </>
                    ) : (
                        <GuidanceInfos
                            notification={notification}
                            notificationIsExternal={notificationIsExternal}
                        />
                    )}
                </>
            </div>
        </div>
    )
}

interface GuidanceInfosProps {
    notificationIsExternal: boolean
    notification: NotificationType
}

const GuidanceInfos = ({
    notificationIsExternal,
    notification
}: GuidanceInfosProps) => {
    return (
        <div className="flex items-center flex-col justify-center h-full w-full px-2">
            <>
                {!notification.u_incident_classification.display_value ? (
                    <div className="flex items-center flex-col justify-center h-full max-w-[290px]">
                        <p className="font-bold text-darkGray/80 text-sm leading-5 text-center break-words">
                            Aguardando preenchimento da Qualidade Corporativa
                        </p>
                        <p className="text-xs mt-2 text-darkGray/80 max-w-[225px] text-center break-words">
                            Apenas para notificações Grave, Óbito ou Risco de
                            imagem
                        </p>
                    </div>
                ) : (
                    <div className="w-full">
                        {notificationIsExternal ? (
                            <>
                                <ExclamationCircleIcon className="w-h-14 h-14 text-yellow" />
                                <p className="text-blueTheme font-bold text-base mt-2 break-words">
                                    Essa é uma notificação externa
                                </p>
                                <p className="self-start mt-10 font-semibold text-sm text-darkGray">
                                    Observação
                                </p>
                                <span className="self-start text-sm text-darkGray break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                    {
                                        notification
                                            ?.u_observation_incident_classification
                                            ?.display_value
                                    }
                                </span>
                            </>
                        ) : (
                            <>
                                <p className="text-blueTheme font-bold text-base mt-2 break-words">
                                    Notificação interna
                                </p>
                                <p className="font-semibold text-sm text-darkGray break-words">
                                    Não será necessário notificar externamente.
                                </p>

                                <p className="self-start mt-10 font-semibold text-sm text-darkGray">
                                    Observação
                                </p>
                                <span className="self-start text-sm text-darkGray break-words" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                                    {
                                        notification
                                            ?.u_observation_incident_classification
                                            ?.display_value
                                    }
                                </span>
                            </>
                        )}
                    </div>
                )}
            </>
        </div>
    )
}
